import { Fragment } from "react";
import { IonHeader, IonLabel, IonTextarea, IonTitle, IonToolbar } from "@ionic/react";
import { DateTime } from "luxon";
import { useAuth } from "pages";
import { createSchoolPath } from "routes/routeData";
import { Card, Colors, TimeInput, useModal } from "swing-components";

import { Button, IconButton } from "~components/Molecules";
import {
  isSameDate,
  TimesTableRowType,
  type TimesTableArrayType,
} from "~components/RequestForm/hooks";
import { SubScheduleDay } from "~components/RequestForm/utils";
import { translate } from "~utils";

import { DaysConflict } from "./";

/***** Constants *****/
// The maximum number of intervals to show in the DayField
const MAX_INTERVALS = 15;

/***** Types *****/
type DayFieldProps = {
  /**
   * The School is required so that we can render the timezone and use the
   * school's ID to create a link to the School's profile.
   */
  school: { id: string; timezone: string };
  /**
   * The intervals for the Request
   */
  intervals: TimesTableArrayType;
  /** Additional Edits */
  additionalEdits: string;

  /*** Handlers ***/
  /**
   * Triggered when the following actions occur:
   * - Removing a day
   * - Changing the start time of a day
   * - Changing the end time of a day
   */
  onIntervalChange: (intervals: TimesTableArrayType) => void;
  /** Triggered when clicking "Add another day" button */
  onAddAnotherDay: () => void;
  /** Triggered when changing the additional edits input */
  onAdditionalEditsChange: (additionalEdits: string) => void;
  /** Whether the field is disabled because the request is waitlisted or filled */
  isWaitlistedOrFilledReq?: boolean;
  /** The end date of the request */
  originalEndDate?: DateTime;
  /** Whether the field is disabled because the request is waitlisted or filled */
  isExtendedRequestEnabled?: boolean;
  /** The sub schedule for the request */
  subSchedule?: Array<SubScheduleDay>;
};

/***** Component *****/
export function DayField(props: DayFieldProps) {
  /*** Props ***/
  // State
  const { intervals, school, additionalEdits } = props;
  // Handlers
  const {
    onIntervalChange,
    onAddAnotherDay,
    onAdditionalEditsChange,
    originalEndDate,
    isWaitlistedOrFilledReq,
    isExtendedRequestEnabled,
    subSchedule,
  } = props;

  /*** Hooks ***/
  const { userInfo: user } = useAuth();

  /*** Constants ***/
  // if Extend Filled/Waitlisted Request, filter out the days that are in the sub schedule
  // Otherwise, just return intervals
  const intervalsWithoutConflicts = isExtendedRequestEnabled
    ? intervals.filter(
        (interval) => !subSchedule?.some((date) => isSameDate(date.start, interval.startTime)),
      )
    : intervals;
  // Count the enabled days
  const days = intervalsWithoutConflicts.filter((interval) => interval.isEnabled).length;
  // When there are more than 15 days, show the "See all days" button and
  // "Additional Edits" section.
  const showSeeAllDaysButton = intervalsWithoutConflicts.length > MAX_INTERVALS;
  // Create an object where the keys are the week number and the values is an
  // array of intervals. This is used to render the days by weeks in the UI.
  const intervalsByWeekNumber = groupIntervalsByWeekNumber({
    // Only show the first 15 days
    intervals: intervalsWithoutConflicts.slice(0, MAX_INTERVALS),
  });
  // Disable the "Add another day" button when the request is waitlisted/filled
  let isAddAnotherDayDisabled = isWaitlistedOrFilledReq;

  // Enable the "Add another day" button when the request is extended
  if (isExtendedRequestEnabled && isWaitlistedOrFilledReq) {
    isAddAnotherDayDisabled = false;
  }

  /*** Handlers ***/
  function handleChange(newInterval: TimesTableRowType) {
    // Find the index of the matching interval
    const index = intervalsWithoutConflicts.findIndex((i) =>
      isSameDate(i.startTime, newInterval.startTime),
    );

    // Then update the interval's values with the new values
    const newIntervals = [...intervalsWithoutConflicts];
    newIntervals[index] = newInterval;

    // And pass the change to the parent.
    onIntervalChange(newIntervals);
  }

  /*** Modal ***/
  const { openModal, closeModal } = useModal({
    component: (
      <SeeAllDaysModal
        intervals={intervalsWithoutConflicts}
        onChange={handleChange}
        onClose={() => closeModal()}
        isWaitlistedOrFilledReq={isWaitlistedOrFilledReq}
        isExtendedRequestEnabled={isExtendedRequestEnabled}
        originalEndDate={originalEndDate}
      />
    ),
  });

  // get the end date of the request...
  const endDate = intervalsWithoutConflicts.at(-1)?.endTime;
  const conflictingDays =
    subSchedule && endDate
      ? subSchedule.filter((day) => day.start <= endDate).map((day) => day.start)
      : [];

  let showMultiDayAdjustmentNotes = showSeeAllDaysButton && !isWaitlistedOrFilledReq;

  if (isExtendedRequestEnabled && isWaitlistedOrFilledReq) {
    showMultiDayAdjustmentNotes = false;
  }

  /*** Render ***/
  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Title */}
      <IonLabel className="font-medium">
        {translate("REQUEST_FORM_SELECT_DATE_DAY_COUNT", { count: days })}
      </IonLabel>

      {/* Show disclaimer which links to School Profile for users with this
      feature flag.*/}
      <p
        css={{ margin: 0, fontSize: 16, fontWeight: 400 }}
        dangerouslySetInnerHTML={{
          __html: translate("REQUEST_FORM_SELECT_DATE_SCHOOL_CLOSURE", {
            // NOTE: Since we are using a `<a />` tag in this copy, we
            // need to handle hash routing ourselves.
            schoolProfileURL: `#${createSchoolPath(school.id)}`,
          }),
        }}
      />

      {/* Show the timezone when the user is a super admin */}
      {user.isSuperAdmin && (
        <p>{translate("REQUEST_FORM_SELECT_DATE_TIMEZONE", { timezone: school.timezone })}</p>
      )}

      {/* List of days */}
      <div>
        {/* For each week */}
        {Object.keys(intervalsByWeekNumber).map((weekNumber, weekIndex, weekArray) => {
          const isLastWeek = weekIndex === weekArray.length - 1;

          // Render every day and a divider between the weeks except the last week
          return (
            <Fragment key={weekNumber}>
              {intervalsByWeekNumber[weekNumber as unknown as number].map(
                (interval, dayIndex, dayArray) => {
                  const isDisabled = checkIfDayIsDisabled({
                    isExtendedRequestEnabled,
                    isWaitlistedOrFilledReq,
                    originalEndDate,
                    days,
                    interval,
                  });

                  return (
                    <SelectableDay
                      key={interval.dateString.toISODate()}
                      startTime={interval.startTime}
                      endTime={interval.endTime}
                      isSelected={interval.isEnabled}
                      onChange={(params) => handleChange({ ...interval, ...params })}
                      // Render an optional School Closure label
                      description={interval.label}
                      isDisabled={isDisabled}
                      // Do not disable the time inputs when the day is the only day
                      // and the request is not waitlisted/filled
                      {...(days === 1 && interval.isEnabled && !isWaitlistedOrFilledReq
                        ? { isTimeDisabled: false }
                        : {})}
                      // Remove the border on the last day of each week
                      hasNoBottomBorder={dayIndex === dayArray.length - 1}
                      isTimeDisabled={isExtendedRequestEnabled && isWaitlistedOrFilledReq}
                    />
                  );
                },
              )}
              {isLastWeek === false && <WeekDivider />}
            </Fragment>
          );
        })}
      </div>

      {/* "Add day" or "See all days" buttons */}
      <div>
        {/* Add another day button */}
        {!showSeeAllDaysButton && (
          <Button
            type="TERTIARY"
            isFluid
            onClick={onAddAnotherDay}
            isDisabled={isAddAnotherDayDisabled}
          >
            {translate("MULTI_DAY_ADD_DATE_LABEL")}
          </Button>
        )}

        {/* See all days button */}
        {showSeeAllDaysButton && (
          <Button type="TERTIARY" isFluid onClick={openModal}>
            {translate("REQUEST_FORM_SELECT_DATE_SEE_ALL_DAYS")}
          </Button>
        )}
      </div>
      {/* Shows the days conflict that a sub might have for a filled request */}
      {subSchedule && conflictingDays.length > 0 && <DaysConflict days={conflictingDays} />}

      {/* Additional Edits */}
      {showMultiDayAdjustmentNotes && (
        <div>
          {/* Label */}
          <IonLabel className="font-medium">
            {translate("REQUEST_FORM_SELECT_DATE_ADDITIONAL_EDITS_LABEL")}
          </IonLabel>

          {/* Text Area */}
          <IonTextarea
            aria-label={translate("REQUEST_FORM_SELECT_DATE_ADDITIONAL_EDITS_LABEL")}
            value={additionalEdits}
            onIonInput={(e) => onAdditionalEditsChange(e.detail.value ?? "")}
            placeholder={translate("REQUEST_FORM_SELECT_DATE_ADDITIONAL_EDITS_PLACEHOLDER")}
            autoGrow
          />
        </div>
      )}
    </div>
  );
}

/***** Helper Components *****/
type SeeAllDaysModalProps = {
  /** The intervals for the Request. */
  intervals: TimesTableArrayType;
  /**
   * Triggered when the following actions occur:
   * - Removing a day
   * - Changing the start time of a day
   * - Changing the end time of a day
   */
  onChange: (newInterval: TimesTableRowType) => void;
  /**
   * Triggered when clicking on "Save X Days" button.
   */
  onClose: () => void;
  /** Whether the field is disabled because the request is waitlisted or filled */
  isWaitlistedOrFilledReq?: boolean;
  /** Whether the request can be extended */
  isExtendedRequestEnabled?: boolean;
  /** The cached end date of the request */
  originalEndDate?: DateTime;
};

function SeeAllDaysModal(props: SeeAllDaysModalProps) {
  const {
    intervals,
    onChange,
    onClose,
    isWaitlistedOrFilledReq,
    isExtendedRequestEnabled,
    originalEndDate,
  } = props;

  /*** Constants ***/
  // The number of enabled days
  const days = intervals.filter((interval) => interval.isEnabled).length;
  // Group intervals by months and weeks
  const intervalsByMonthAndWeekNumber = groupIntervalsByMonthAndWeekNumber({ intervals });

  return (
    <div
      css={{
        backgroundColor: Colors.white300,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/*
        TODO: We should create a simple ModalHeader component in
        swing-components to simplify this and make is consistent
        https://linear.app/swing-education/issue/MKTP-689/[design-system]-modal
      */}
      {/* Header */}
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("REQUEST_FORM_SELECT_DATE_MODAL_HEADER")}</IonTitle>
        </IonToolbar>
      </IonHeader>

      {/* Content */}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          padding: 16,
          overflow: "auto",
        }}
      >
        {/* Card with Intervals grouped by months and weeks */}
        <Card>
          {Object.keys(intervalsByMonthAndWeekNumber).map((month) => {
            return (
              <Fragment key={month}>
                {/* Month Header */}
                <div
                  css={{
                    // Keep the button shown at the bottom of the modal
                    position: "sticky",
                    // These styles is to offset the Modal and Card padding, and to
                    // hide the overflow elements with the background color usage.
                    top: -16,
                    marginTop: -16,
                    paddingTop: 16,
                    backgroundColor: Colors.white200,
                    zIndex: 1,
                  }}
                >
                  <div
                    css={{
                      backgroundColor: Colors.teal200,
                      padding: 16,
                      borderRadius: 8,
                    }}
                  >
                    <p
                      css={{
                        margin: 0,
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    >
                      {month}
                    </p>
                  </div>
                </div>

                {/* For each week */}
                {Object.keys(intervalsByMonthAndWeekNumber[month]).map(
                  (weekNumber, weekIndex, weekArray) => {
                    const isLastWeek = weekIndex === weekArray.length - 1;

                    // Render every day and a divider between the weeks except the last week
                    return (
                      <Fragment key={`week-${weekNumber}`}>
                        {intervalsByMonthAndWeekNumber[month][weekNumber as unknown as number].map(
                          (interval, dayIndex, dayArray) => {
                            const isDisabled = checkIfDayIsDisabled({
                              isExtendedRequestEnabled,
                              isWaitlistedOrFilledReq,
                              originalEndDate,
                              days,
                              interval,
                            });

                            return (
                              <SelectableDay
                                key={interval.dateString.toISODate()}
                                startTime={interval.startTime}
                                endTime={interval.endTime}
                                isSelected={interval.isEnabled}
                                onChange={(params) => onChange({ ...interval, ...params })}
                                // Render an optional School Closure description
                                description={interval.label}
                                isDisabled={isDisabled}
                                // Do not disable the time inputs when the day is the only day
                                // and the request is not waitlisted/filled
                                {...(days === 1 && interval.isEnabled && !isWaitlistedOrFilledReq
                                  ? { isTimeDisabled: false }
                                  : {})}
                                // Remove the border on the last day of each week
                                hasNoBottomBorder={dayIndex === dayArray.length - 1}
                                isTimeDisabled={isExtendedRequestEnabled && isWaitlistedOrFilledReq}
                              />
                            );
                          },
                        )}
                        {isLastWeek === false && <WeekDivider />}
                      </Fragment>
                    );
                  },
                )}
              </Fragment>
            );
          })}
        </Card>

        {/* Save Button */}
        <div
          css={{
            // Keep the button shown at the bottom of the modal
            position: "sticky",
            // To render above the sticky month headers
            zIndex: 2,
            // These styles is to offset the Modal and Card padding, and to
            // hide the overflow elements with the background color usage.
            bottom: -16,
            marginBottom: -16,
            paddingBottom: 16,
            backgroundColor: Colors.white300,
          }}
        >
          <Button type="PRIMARY" isFluid onClick={onClose}>
            {translate("REQUEST_FORM_SELECT_DATE_MODAL_SAVE_LABEL", { days })}
          </Button>
        </div>
      </div>
    </div>
  );
}

type SelectableDayProps = {
  /*** Required ***/
  /** The start time of the day */
  startTime: DateTime;
  /** The end time of the day */
  endTime: DateTime;
  /** Whether the day is selected */
  isSelected: boolean;
  /** Triggered when toggling the day, or changing the start or end time */
  onChange: (params: { isEnabled?: boolean; startTime?: DateTime; endTime?: DateTime }) => void;

  /*** Optional ***/
  /**
   * Render an optional description under the day
   */
  description?: string;
  /**
   * Disabled the checkbox but not the time inputs.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Controls whether time inputs are disabled when isSelected is true.
   * When not specified, defaults to isDisabled's value.
   * @default isDisabled
   */
  isTimeDisabled?: boolean;
  /**
   * Show a border at the bottom of the component.
   * @default false
   */
  hasNoBottomBorder?: boolean;
};

/**
 * A selectable day in a Request with the ability to edit the start and end time.
 *
 * @Figma https://www.figma.com/design/DpTkGJFsgHRtrFdosjMWok/%F0%9F%97%93%EF%B8%8F-LTR-%2B-Non-School-Days?node-id=1667-101600&t=elZ4s1uatu9vxff0-11
 */
function SelectableDay(props: SelectableDayProps) {
  const {
    startTime,
    endTime,
    isSelected,
    onChange,
    description,
    isDisabled = false,
    isTimeDisabled,
    hasNoBottomBorder,
  } = props;

  // Time inputs are disabled if:
  // - The day is not selected, OR
  // - The day is selected AND:
  //   - isTimeDisabled is explicitly set (takes precedence), OR
  //   - otherwise fallback to isDisabled
  const timeInputDisabled =
    !isSelected || (isSelected && (isTimeDisabled !== undefined ? isTimeDisabled : isDisabled));

  return (
    // Container
    <div
      css={{
        paddingTop: 16,
        paddingBottom: 16, // Using box shadow to create the border to not increase the height of
        // the container by 1px.
        boxShadow: hasNoBottomBorder ? "none" : `inset 0 -1px 0 ${Colors.slate200}`,

        // Disabled text styles
        ...((!isSelected || isDisabled) && {
          color: Colors.slate400,
        }),
      }}
    >
      {/* Top Row */}
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: 8,

          // Disabled text styles
          ...(isSelected === false && {
            textDecoration: "line-through",
          }),
        }}
      >
        {/* Checkbox */}
        <IconButton
          onClick={() => onChange({ isEnabled: !isSelected })}
          aria-label={isSelected ? "Enabled" : "Disabled"}
          // TODO: This logic would make sense in a Checkbox component
          iconProps={{
            name: isSelected ? "Check Square" : "Square",
            color: isDisabled ? Colors.slate300 : Colors.blue500,
          }}
          disabled={isDisabled}
          // TODO: This is required since the height is 28px without this.
          css={{
            width: 24,
            height: 24,
            ...(isDisabled && { cursor: "not-allowed !important" }),
          }}
        />

        {/* Date */}
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            gap: 4,
            // Using a "magic" number here since we want to align the day numbers
            // row by row. This number has been determined via Figma and testing
            // this with the widest non-monospace font characters which are "Wed"
            // and "May 28".
            //
            // Display grid would resolve this, however, with the UI breakdown
            // with dynamic dividers, this is easier.
            width: 92,
            whiteSpace: "nowrap",
            p: { margin: 0 },
          }}
        >
          {/* Short Day */}
          <p>{startTime.toFormat("ccc")}</p>
          {/* Date */}
          <p>{startTime.toFormat("MMM d")}</p>
        </div>

        {/* Times */}
        <div
          css={{
            // Pushed this container to the far right
            marginLeft: "auto",
            display: "flex",
            gap: 4,
            alignItems: "center",
            textAlign: "center",
            // Override time input color when isTimeDisabled is false
            ...(!timeInputDisabled && {
              "& input": {
                color: `${Colors.black} !important`,
              },
            }),
          }}
        >
          <TimeInput
            value={startTime}
            onChange={(newStartTime) => onChange({ startTime: newStartTime })}
            isDisabled={timeInputDisabled}
            showTextInputOnDesktop
          />
          -
          <TimeInput
            value={endTime}
            onChange={(newEndTime) => onChange({ endTime: newEndTime })}
            isDisabled={timeInputDisabled}
            showTextInputOnDesktop
          />
        </div>
      </div>
      {/* Options Description */}
      {description && (
        <p
          css={{
            marginTop: 4,
            marginBottom: 0,
            paddingLeft: 32,
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          {description}
        </p>
      )}
    </div>
  );
}

/**
 * Divider between weeks
 */
function WeekDivider() {
  return <div css={{ height: 2, backgroundColor: Colors.slate300 }}></div>;
}

/***** Helper Functions ****/
/**
 * Groups intervals by week number.
 *
 * @example
 * {
 *   "2025-W01": [interval1, interval2],
 *   "2025-W02": [interval3, interval4],
 * }
 */
type IntervalsByWeekNumber = Record<string, TimesTableRowType[]>;
export function groupIntervalsByWeekNumber(params: {
  intervals: TimesTableArrayType;
}): IntervalsByWeekNumber {
  const { intervals } = params;

  return intervals.reduce((acc, interval) => {
    const year = interval.startTime.year;
    const weekNumber = interval.startTime.weekNumber;
    const key = `${year}-W${weekNumber}`;

    if (acc[key] === undefined) {
      acc[key] = [interval];
    } else {
      acc[key].push(interval);
    }

    return acc;
  }, {} as IntervalsByWeekNumber);
}

/**
 * Groups intervals by month and week numbers.
 *
 * @example
 * {
 *   "January 2025": {
 *     "2025-W1": [interval1, interval2],
 *     "2025-W2": [interval3, interval4],
 *   },
 * }
 */
type IntervalsByMonth = Record<string, IntervalsByWeekNumber>;
export function groupIntervalsByMonthAndWeekNumber(params: {
  intervals: TimesTableArrayType;
}): IntervalsByMonth {
  const { intervals } = params;

  // Determine the unique month and year combinations in the intervals.
  const uniqueMonths = [...new Set(intervals.map((i) => i.startTime.toFormat("MMMM yyyy")))];

  return uniqueMonths.reduce((acc, month) => {
    // If the month doesn't exist, initialize it with an empty object
    if (acc[month] === undefined) {
      acc[month] = {};
    }

    // Get all intervals for this month
    const monthIntervals = intervals.filter((i) => i.startTime.toFormat("MMMM yyyy") === month);

    // Use groupIntervalsByWeek to handle the week grouping logic
    acc[month] = groupIntervalsByWeekNumber({ intervals: monthIntervals });

    return acc;
  }, {} as IntervalsByMonth);
}

type IsDisabledProps = {
  /** Whether the request can be extended */
  isExtendedRequestEnabled?: boolean;
  /** Whether the request is waitlisted or filled */
  isWaitlistedOrFilledReq?: boolean;
  /** The cached end date of the request */
  originalEndDate?: DateTime;
  /** The number of days in the request */
  days: number;
  /** The interval to check */
  interval: TimesTableRowType;
};

/**
 * Check if the day is disabled.
 *
 * @description
 * - Enable the day when:
 *   - The request can be extended
 *   - The request is waitlisted/filled
 *   - The cached end date is set
 *   - The interval's start time is greater than or equal to the cached end date
 * - Otherwise, disable the day when it's the only day
 *   - OR when the request is waitlisted/filled
 */
function checkIfDayIsDisabled(props: IsDisabledProps) {
  const { isExtendedRequestEnabled, isWaitlistedOrFilledReq, originalEndDate, days, interval } =
    props;
  return isExtendedRequestEnabled &&
    isWaitlistedOrFilledReq &&
    originalEndDate &&
    interval.startTime >= originalEndDate
    ? false
    : (days === 1 && interval.isEnabled) || isWaitlistedOrFilledReq;
}
